import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Check, ChevronLeft, ChevronRight, Clock, User, Mail, Phone, 
  Loader2, CreditCard, Banknote, Calendar as CalendarIcon, Filter, MessageSquare,
  Paperclip, X, FileText, Image as ImageIcon, Upload
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, isBefore, startOfToday } from 'date-fns';
import { sl } from 'date-fns/locale';
import { Gift, Tag } from 'lucide-react';

const API_URL = '/api';

// Separate component for time slot to avoid babel crash
const TimeSlotButton = ({ slot, isSelected, onSelect, onHover, onLeave, isHovered, therapistNames }) => {
  return (
    <div className="relative">
      <button
        onClick={() => onSelect(slot)}
        onMouseEnter={() => onHover(slot.time)}
        onMouseLeave={onLeave}
        className={`w-full py-2 text-xs rounded-lg transition-all ${
          isSelected
            ? 'bg-[#f87171] text-white font-semibold'
            : 'bg-slate-100 hover:bg-[#f87171]/20 text-slate-700'
        }`}
        data-testid={`time-slot-${slot.time}`}
      >
        {slot.time}
      </button>
      
      {isHovered && !isSelected && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 pointer-events-none">
          <div className="bg-[#1a365d] text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap">
            <div className="font-medium mb-1">Na voljo:</div>
            <div>{therapistNames}</div>
            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#1a365d]" />
          </div>
        </div>
      )}
    </div>
  );
};

// Separate component for therapist filter button
const FilterButton = ({ therapist, isActive, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 text-xs rounded-full transition-all ${
        isActive 
          ? 'bg-[#1a365d] text-white' 
          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
      }`}
    >
      {therapist.name}
    </button>
  );
};

const BookingModal = ({ isOpen, onClose, preselectedService, prefilledSlot }) => {
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [availableTherapists, setAvailableTherapists] = useState([]); // Therapists with at least 1 slot for current date/service
  const [selectedService, setSelectedService] = useState(null);
  const [selectedTherapist, setSelectedTherapist] = useState(null);
  const [therapistFilter, setTherapistFilter] = useState('all');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [allSlots, setAllSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('location');
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', notes: '' });
  const [hoveredSlot, setHoveredSlot] = useState(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  
  // Attachments state
  const [attachments, setAttachments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef(null);
  
  // Voucher state
  const [voucherCode, setVoucherCode] = useState('');
  const [voucherInfo, setVoucherInfo] = useState(null); // { valid, remaining_amount, voucher }
  const [validatingVoucher, setValidatingVoucher] = useState(false);

  // Load saved user data from localStorage when modal opens
  useEffect(() => {
    if (isOpen) {
      const savedData = localStorage.getItem('booking_user_data');
      if (savedData) {
        try {
          const parsed = JSON.parse(savedData);
          setFormData({
            name: parsed.name || '',
            email: parsed.email || '',
            phone: parsed.phone || '',
            notes: ''
          });
          setRememberMe(true);
          // If user previously saved data, they already accepted GDPR
          if (parsed.gdprAccepted) {
            setTermsAccepted(true);
          }
        } catch (e) {
          console.error('Error loading saved data:', e);
        }
      }
    }
  }, [isOpen]);

  // Clear attachments and reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setAttachments([]);
      setSelectedTime(null);
      setSelectedSlot(null);
      setStep(1);
      setSelectedService(null);
      setSelectedDate(null);
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const servicesRes = await axios.get(`${API_URL}/services`);
        const therapistsRes = await axios.get(`${API_URL}/therapists`);
        
        setServices(servicesRes.data);
        
        const defaultT = { id: 'default', name: 'Saša Panič', title: 'Diplomirana fizioterapevtka', photo: '/images/sasa-panic-fizioterapevtka-ljubljana.avif' };
        const others = therapistsRes.data.filter(t => t.id !== 'default' && t.name !== 'Saša Panič');
        setTherapists([defaultT, ...others]);
      } catch (error) {
        console.error('Error:', error);
      }
    };
    if (isOpen) fetchData();
  }, [isOpen]);

  useEffect(() => {
    if (preselectedService && services.length > 0) {
      const service = services.find(s => s.id === preselectedService.id) || preselectedService;
      setSelectedService(service);
      setStep(2);
    }
  }, [preselectedService, services]);

  // ── SERVICE-THERAPIST COMPATIBILITY (mirror of backend hard rules) ──────
  // Keep in sync with backend `_validate_service_therapist_compat` in
  // /app/backend/server.py so the UI never lets the customer pick a
  // combination that will later be rejected server-side.
  const isTherapistEligibleForService = React.useCallback((therapist, service) => {
    if (!therapist || !service) return true;
    const baseId = (therapist.id || '').split('_room_')[0];
    const sid = service.id?.toString();
    const sname = (service.name || '').toLowerCase();
    const scategory = service.category || '';
    const isDryNeedling = sid === '4' || sname.includes('dry needling') || sname.includes('dry-needling');
    const isMassage = scategory === 'masaze' || sname.includes('masaža');
    const isPregnancyMassage = sid === '5b' || (isMassage && (sname.includes('nosečnic') || sname.includes('nosecnic')));
    // HARD business rules override any per-therapist allowed_services
    if (isDryNeedling && baseId !== 'default') return false;
    if (isPregnancyMassage && baseId !== 'default') return false; // pregnancy massage = only Saša
    if (isMassage && !isPregnancyMassage && baseId === 'default') return false;
    // Special services are governed solely by hard rules above
    if (isDryNeedling || isPregnancyMassage) return true;
    // Per-therapist whitelist (empty = all allowed)
    const allowed = therapist.allowed_services;
    if (allowed && allowed.length > 0) return allowed.includes(sid);
    return true;
  }, []);

  // Helper: select service and either skip to step 3 (when slot pre-filled) or go to step 2 (calendar)
  const handleSelectService = (service) => {
    // If we got here via Quick-Booking prefill (specific slot + specific
    // therapist), we MUST NOT silently swap the therapist to someone who
    // does perform the service — that other person might not even be free
    // at this slot. Instead we tell the customer the service is not
    // available for this slot and let them pick a different service.
    if (prefilledSlot && prefilledSlot.therapistId && prefilledSlot.date && prefilledSlot.time) {
      const prefT = therapists.find(t => t.id === prefilledSlot.therapistId);
      if (prefT && !isTherapistEligibleForService(prefT, service)) {
        toast.error(
          `Ta storitev ni na voljo za ${prefT.name} v izbranem terminu. Prosimo izberite drugo storitev, ali si ogledajte koledar za druge proste termine.`,
          { duration: 6000 }
        );
        // Stay on step 1 — do not switch therapist, do not advance.
        return;
      }
    }

    setSelectedService(service);
    if (prefilledSlot && prefilledSlot.date && prefilledSlot.time) {
      // Apply pre-filled slot from URL params (Quick Booking pill)
      const dateObj = new Date(`${prefilledSlot.date}T00:00:00`);
      setSelectedDate(dateObj);
      setSelectedTime(prefilledSlot.time);
      if (prefilledSlot.therapistId) {
        const t = therapists.find(t => t.id === prefilledSlot.therapistId);
        if (t) setSelectedTherapist(t);
      }
      setStep(3);
    } else {
      setStep(2);
    }
  };

  useEffect(() => {
    if (selectedDate && selectedService && therapists.length > 0) {
      const fetchAllSlots = async () => {
        setLoadingSlots(true);
        try {
          const dateStr = format(selectedDate, 'yyyy-MM-dd');
          const slotsMap = {};
          const therapistsWithSlots = new Set(); // Track which therapists have slots
          
          // Filter therapists who can perform the selected service.
          // Uses same rules as backend (`isTherapistEligibleForService`)
          // including HARD business rules (Dry Needling → only default,
          // Massage → NOT default) so a misconfigured `allowed_services`
          // cannot leak an incompatible therapist into the UI.
          const eligibleTherapists = therapists.filter(t => isTherapistEligibleForService(t, selectedService));
          
          for (const therapist of eligibleTherapists) {
            try {
              const response = await axios.get(`${API_URL}/smart-slots/${dateStr}`, {
                params: { service_id: selectedService.id, therapist_id: therapist.id }
              });
              const slots = response.data.slots || [];
              for (const slotData of slots) {
                // Handle both string and object formats
                const timeStr = typeof slotData === 'string' ? slotData : slotData.time;
                if (!slotsMap[timeStr]) {
                  slotsMap[timeStr] = [];
                }
                slotsMap[timeStr].push({ id: therapist.id, name: therapist.name, photo: therapist.photo, title: therapist.title });
                therapistsWithSlots.add(therapist.id); // Mark this therapist as having slots
              }
            } catch (e) {
              // ignore
            }
          }
          
          const combined = Object.keys(slotsMap).sort().map(timeKey => ({
            time: timeKey,
            therapists: slotsMap[timeKey],
            therapistNames: slotsMap[timeKey].map(t => t.name).join(', ')
          }));
          
          setAllSlots(combined);
          // Only show therapists who actually have available slots
          setAvailableTherapists(therapists.filter(t => therapistsWithSlots.has(t.id)));
        } catch (error) {
          setAllSlots([]);
          setAvailableTherapists([]);
        } finally {
          setLoadingSlots(false);
        }
      };
      fetchAllSlots();
    }
  }, [selectedDate, selectedService, therapists, isTherapistEligibleForService]);

  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setStep(1);
        setSelectedService(null);
        setSelectedTherapist(null);
        setTherapistFilter('all');
        setSelectedDate(null);
        setSelectedTime(null);
        setSelectedSlot(null);
        setCurrentMonth(new Date());
        setPaymentMethod('location');
        setFormData({ name: '', email: '', phone: '' });
        setHoveredSlot(null);
        setTermsAccepted(false);
        setVoucherCode('');
        setVoucherInfo(null);
      }, 300);
    }
  }, [isOpen]);

  const handleSlotSelect = (slot) => {
    setSelectedTime(slot.time);
    setSelectedSlot(slot);
    if (slot.therapists.length === 1) {
      setSelectedTherapist(slot.therapists[0]);
    } else if (therapistFilter !== 'all') {
      const filtered = slot.therapists.find(t => t.id === therapistFilter);
      setSelectedTherapist(filtered || slot.therapists[0]);
    } else {
      setSelectedTherapist(slot.therapists[0]);
    }
  };

  // File upload handler
  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    // Check max files
    if (attachments.length + files.length > 3) {
      toast.error('Največ 3 datoteke so dovoljene');
      return;
    }
    
    for (const file of files) {
      // Check file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`Datoteka ${file.name} je prevelika (max 5MB)`);
        continue;
      }
      
      // Check file type
      const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error(`Nepodprt tip datoteke: ${file.name}`);
        continue;
      }
      
      setUploadingFile(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API_URL}/upload/attachment`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setAttachments(prev => [...prev, {
          id: response.data.id,
          name: response.data.filename,
          size: response.data.size,
          type: response.data.type
        }]);
        
        toast.success(`Datoteka ${file.name} naložena`);
      } catch (err) {
        toast.error(`Napaka pri nalaganju: ${file.name}`);
      }
      setUploadingFile(false);
    }
    
    // Reset input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  
  // Remove attachment
  const removeAttachment = async (id) => {
    try {
      await axios.delete(`${API_URL}/attachments/${id}`);
      setAttachments(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      console.error('Error removing attachment:', err);
    }
  };
  
  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const validateVoucher = async () => {
    if (!voucherCode.trim()) return;
    setValidatingVoucher(true);
    try {
      const res = await axios.get(`${API_URL}/vouchers/validate/${voucherCode.trim()}`);
      setVoucherInfo(res.data);
      if (res.data.valid) {
        toast.success(`Bon veljaven! Na voljo: ${res.data.remaining_amount}€`);
      } else {
        toast.error(res.data.reason || 'Bon ni veljaven');
      }
    } catch (err) {
      setVoucherInfo(null);
      toast.error('Koda bona ni veljavna');
    } finally {
      setValidatingVoucher(false);
    }
  };

  const clearVoucher = () => {
    setVoucherCode('');
    setVoucherInfo(null);
  };

  // Calculate effective price after voucher
  const servicePrice = selectedService?.price || 0;
  const voucherDiscount = voucherInfo?.valid ? Math.min(voucherInfo.remaining_amount, servicePrice) : 0;
  const finalPrice = servicePrice - voucherDiscount;
  const voucherCoversAll = voucherDiscount >= servicePrice;

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Izpolnite vsa polja');
      return;
    }
    
    // Save user data if "Remember me" is checked (includes GDPR acceptance)
    if (rememberMe) {
      localStorage.setItem('booking_user_data', JSON.stringify({
        ...formData,
        gdprAccepted: true
      }));
    } else {
      localStorage.removeItem('booking_user_data');
    }
    
    setIsSubmitting(true);
    try {
      // CRITICAL: Check availability one more time before submitting to prevent double booking
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const availCheck = await axios.get(`${API_URL}/bookings/check-availability`, {
        params: { 
          date: dateStr, 
          time: selectedTime, 
          therapist_id: selectedTherapist?.id || 'default' 
        }
      });
      
      if (!availCheck.data.available) {
        toast.error('Ta termin je bil pravkar zaseden. Prosimo izberite drug čas.');
        // Refresh available slots
        setSelectedTime(null);
        setSelectedSlot(null);
        setIsSubmitting(false);
        return;
      }
      
      const bookingResponse = await axios.post(`${API_URL}/bookings`, {
        therapist_id: selectedTherapist?.id || 'default',
        service_id: selectedService.id,
        service_name: selectedService.name,
        date: dateStr,
        time: selectedTime,
        client_name: formData.name,
        client_email: formData.email,
        client_phone: formData.phone,
        notes: formData.notes || '',
        payment_method: voucherCoversAll ? 'voucher' : paymentMethod,
        attachment_ids: attachments.map(a => a.id),
        voucher_code: voucherInfo?.valid ? voucherCode.trim() : undefined
      });
      
      const bookingId = bookingResponse.data.id;
      
      // If voucher covers full price, no payment needed
      if (voucherCoversAll) {
        toast.success('Termin uspešno rezerviran z darilnim bonom!');
        onClose();
        return;
      }
      
      if (paymentMethod === 'card') {
        const paymentResponse = await axios.post(`${API_URL}/payments/create-checkout`, {
          payment_type: 'booking', reference_id: bookingId, origin_url: window.location.origin
        });
        window.location.href = paymentResponse.data.checkout_url;
        return;
      }
      
      if (paymentMethod === 'paypal') {
        const paymentResponse = await axios.post(`${API_URL}/payments/paypal/create`, {
          payment_type: 'booking', reference_id: bookingId, origin_url: window.location.origin
        });
        window.location.href = paymentResponse.data.approval_url;
        return;
      }
      
      toast.success('Termin uspešno rezerviran!');
      onClose();
    } catch (error) {
      // Show specific error message from backend if available
      const errorMsg = error.response?.data?.detail || 'Napaka pri rezervaciji. Prosimo poskusite znova.';
      toast.error(errorMsg);
      
      // If slot is taken, clear selection and let user choose again
      if (errorMsg.includes('zaseden') || errorMsg.includes('zasedeni')) {
        setSelectedTime(null);
        setSelectedSlot(null);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const today = startOfToday();
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDay = monthStart.getDay();
  const adjustedStartDay = startDay === 0 ? 6 : startDay - 1;
  
  const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  const filteredSlots = therapistFilter === 'all' 
    ? allSlots 
    : allSlots.filter(slot => slot.therapists.some(t => t.id === therapistFilter));

  const dayLabels = ['Po', 'To', 'Sr', 'Če', 'Pe', 'So', 'Ne'];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md p-0 overflow-hidden rounded-2xl bg-white [&>button.absolute]:hidden">
        <div className="bg-[#1a365d] px-5 py-3 text-white flex items-center justify-between">
          <h2 className="text-lg font-bold">Rezervacija</h2>
          <div className="flex items-center gap-3">
            <div className="flex gap-1">
              <div className={`w-6 h-1 rounded-full ${step >= 1 ? 'bg-[#f87171]' : 'bg-white/30'}`} />
              <div className={`w-6 h-1 rounded-full ${step >= 2 ? 'bg-[#f87171]' : 'bg-white/30'}`} />
              <div className={`w-6 h-1 rounded-full ${step >= 3 ? 'bg-[#f87171]' : 'bg-white/30'}`} />
              <div className={`w-6 h-1 rounded-full ${step >= 4 ? 'bg-[#f87171]' : 'bg-white/30'}`} />
            </div>
            {/* Custom close (Radix default X is hidden — invisible on dark header) */}
            <button
              type="button"
              onClick={onClose}
              aria-label="Zapri"
              data-testid="booking-modal-close"
              className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 active:bg-white/35 flex items-center justify-center transition ring-1 ring-white/20"
            >
              <X className="w-5 h-5 text-white" strokeWidth={2.5} />
            </button>
          </div>
        </div>

        <div className="p-4 max-h-[70vh] overflow-y-auto bg-white">
          {step === 1 && (
            <div data-testid="step-service">
              {(() => {
                // Category display config
                const CATEGORY_CONFIG = {
                  'izpostavljeno': { title: 'Izpostavljeno', style: 'featured' },
                  'terapije': { title: 'Terapije', style: 'normal' },
                  'masaze': { title: 'Masaže', style: 'normal' },
                  'nosecnost': { title: 'Nosečnost & porod', style: 'normal' },
                  'paketi': { title: 'Paketi - prihranite', style: 'package' },
                  'ostalo': { title: 'Ostalo', style: 'normal' }
                };
                
                // Featured colors for top 3
                const FEATURED_COLORS = [
                  'border-[#f87171] bg-gradient-to-br from-[#f87171]/10 to-[#f87171]/5', // Rose/coral
                  'border-blue-400 bg-gradient-to-br from-blue-100 to-blue-50', // Blue
                  'border-purple-400 bg-gradient-to-br from-purple-100 to-purple-50', // Purple
                ];
                
                const FEATURED_TEXT_COLORS = [
                  'text-[#f87171]',
                  'text-blue-500',
                  'text-purple-500',
                ];
                
                // Group services by category
                const groupedServices = {};
                services.forEach(s => {
                  const cat = s.category || 'ostalo';
                  if (!groupedServices[cat]) groupedServices[cat] = [];
                  groupedServices[cat].push(s);
                });
                
                const categoryOrder = ['izpostavljeno', 'terapije', 'masaze', 'nosecnost', 'paketi', 'ostalo'];
                
                const ServiceCard = ({ service, featured = false, colorIndex = 0 }) => {
                  // If a specific therapist was prefilled via URL, mark
                  // services they do NOT perform so the customer knows
                  // upfront and doesn't hit an error toast after clicking.
                  const prefT = (prefilledSlot?.therapistId)
                    ? therapists.find(t => t.id === prefilledSlot.therapistId)
                    : null;
                  const notOfferedByPrefill = prefT ? !isTherapistEligibleForService(prefT, service) : false;
                  const borderColor = featured ? FEATURED_COLORS[colorIndex % 3] : 'border-slate-200 hover:border-[#f87171]';
                  const priceColor = featured ? FEATURED_TEXT_COLORS[colorIndex % 3] : 'text-[#f87171]';
                  
                  return (
                    <button
                      key={service.id}
                      onClick={() => handleSelectService(service)}
                      className={`w-full text-left rounded-xl border-2 transition-all hover:shadow-md ${
                        featured ? `${borderColor} p-4` : `${borderColor} p-3`
                      } ${notOfferedByPrefill ? 'opacity-50 hover:opacity-70' : ''}`}
                      data-testid={`service-${service.id}`}
                    >
                      <div className="flex justify-between items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <p className={`font-semibold text-[#1a365d] leading-tight ${featured ? 'text-base' : 'text-sm'}`}>
                            {service.name}
                          </p>
                          {notOfferedByPrefill && prefT && (
                            <p className="text-[10px] text-amber-700 font-semibold mt-0.5 uppercase tracking-wide" data-testid={`service-unavailable-${service.id}`}>
                              Ni na voljo z {prefT.name}
                            </p>
                          )}
                          {featured && service.description && !notOfferedByPrefill && (
                            <p className="text-slate-500 text-xs mt-1 line-clamp-2">{service.description}</p>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className={`font-bold ${priceColor} ${featured ? 'text-lg' : 'text-sm'}`}>{service.price}€</p>
                          <p className="text-slate-400 text-xs">{service.duration} min</p>
                        </div>
                      </div>
                    </button>
                  );
                };
                
                const PackageCard = ({ service }) => (
                  <button
                    key={service.id}
                    onClick={() => { setSelectedService(service); setStep(2); }}
                    className="w-full text-left rounded-xl border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 p-3 transition-all hover:shadow-md hover:border-emerald-400"
                    data-testid={`service-${service.id}`}
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm">📦</span>
                          <p className="font-semibold text-[#1a365d] text-sm">{service.name}</p>
                        </div>
                        {service.description && <p className="text-emerald-600 text-xs mt-1">{service.description}</p>}
                      </div>
                      <div className="text-right flex-shrink-0 ml-2">
                        <p className="font-bold text-emerald-600 text-base">{service.price}€</p>
                      </div>
                    </div>
                  </button>
                );
                
                return (
                  <div className="space-y-4">
                    {categoryOrder.map(catKey => {
                      const catServices = groupedServices[catKey] || [];
                      if (catServices.length === 0) return null;
                      
                      const config = CATEGORY_CONFIG[catKey] || CATEGORY_CONFIG['ostalo'];
                      
                      // Featured section with different colors
                      if (config.style === 'featured') {
                        return (
                          <div key={catKey}>
                            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                              <span>⭐</span> {config.title}
                            </p>
                            <div className="space-y-2">
                              {catServices.map((s, i) => (
                                <ServiceCard key={s.id} service={s} featured={true} colorIndex={i} />
                              ))}
                            </div>
                          </div>
                        );
                      }
                      
                      if (config.style === 'package') {
                        return (
                          <div key={catKey} className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-3 border border-emerald-200">
                            <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-2 flex items-center gap-1">
                              <span>📦</span> {config.title}
                            </p>
                            <div className="space-y-2">
                              {catServices.map(s => <PackageCard key={s.id} service={s} />)}
                            </div>
                          </div>
                        );
                      }
                      
                      return (
                        <div key={catKey}>
                          {config.title && (
                            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">{config.title}</p>
                          )}
                          <div className="space-y-2">
                            {catServices.map((s, i) => (
                              <ServiceCard key={s.id} service={s} featured={false} />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}

          {step === 2 && (
            <div data-testid="step-calendar">
              <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                <div className="w-8 h-8 bg-[#f87171]/10 rounded-lg flex items-center justify-center">
                  <CalendarIcon className="w-4 h-4 text-[#f87171]" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm text-[#1a365d]">{selectedService?.name}</p>
                </div>
                <span className="text-[#f87171] font-bold">{selectedService?.price}€</span>
              </div>

              <div className="mb-3">
                <div className="flex items-center justify-between mb-2">
                  <button onClick={prevMonth} className="p-1 hover:bg-slate-100 rounded" data-testid="prev-month">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="font-semibold text-[#1a365d] text-sm">
                    {format(currentMonth, 'LLLL yyyy', { locale: sl })}
                  </span>
                  <button onClick={nextMonth} className="p-1 hover:bg-slate-100 rounded" data-testid="next-month">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="grid grid-cols-7 gap-1 text-center text-xs mb-1">
                  {dayLabels.map(day => (
                    <div key={day} className="text-slate-400 font-medium py-1">{day}</div>
                  ))}
                </div>
                
                <div className="grid grid-cols-7 gap-1">
                  {Array(adjustedStartDay).fill(null).map((_, i) => (
                    <div key={`empty-${i}`} />
                  ))}
                  {calendarDays.map((day) => {
                    const isPast = isBefore(day, today);
                    const isSelected = selectedDate && isSameDay(day, selectedDate);
                    const isTodayDate = isToday(day);
                    const dayNum = format(day, 'd');
                    
                    return (
                      <button
                        key={day.toISOString()}
                        onClick={() => !isPast && setSelectedDate(day)}
                        disabled={isPast}
                        className={`p-1.5 text-xs rounded-lg transition-all ${
                          isPast ? 'text-slate-300 cursor-not-allowed' :
                          isSelected ? 'bg-[#f87171] text-white font-bold' :
                          isTodayDate ? 'bg-[#f87171]/10 text-[#f87171] font-semibold' :
                          'hover:bg-slate-100'
                        }`}
                        data-testid={`calendar-day-${dayNum}`}
                      >
                        {dayNum}
                      </button>
                    );
                  })}
                </div>
              </div>

              {selectedDate && availableTherapists.length >= 1 && !loadingSlots && (
                <div className="mb-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Filter className="w-4 h-4 text-slate-400" />
                    <span className="text-xs text-slate-500">Prosti terapevti:</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      onClick={() => setTherapistFilter('all')}
                      className={`px-3 py-1.5 text-xs rounded-full transition-all ${
                        therapistFilter === 'all' ? 'bg-[#1a365d] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      Vsi
                    </button>
                    {availableTherapists.map(t => (
                      <FilterButton key={t.id} therapist={t} isActive={therapistFilter === t.id} onClick={() => setTherapistFilter(t.id)} />
                    ))}
                  </div>
                </div>
              )}

              {selectedDate && (
                <div className="mb-3">
                  <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Prosti termini za {format(selectedDate, 'd. MMMM', { locale: sl })}:
                  </p>
                  
                  {loadingSlots ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="w-5 h-5 animate-spin text-[#f87171]" />
                    </div>
                  ) : filteredSlots.length > 0 ? (
                    <div className="grid grid-cols-4 gap-1.5 relative">
                      {filteredSlots.map((slot) => {
                        const slotTime = slot.time;
                        const slotTherapistNames = slot.therapistNames;
                        const isSlotSelected = selectedTime === slotTime;
                        const isSlotHovered = hoveredSlot === slotTime;
                        
                        return (
                          <div key={slotTime} className="relative">
                            <button
                              onClick={() => handleSlotSelect(slot)}
                              onMouseEnter={() => setHoveredSlot(slotTime)}
                              onMouseLeave={() => setHoveredSlot(null)}
                              className={`w-full py-2 text-xs rounded-lg transition-all ${
                                isSlotSelected
                                  ? 'bg-[#f87171] text-white font-semibold'
                                  : 'bg-slate-100 hover:bg-[#f87171]/20 text-slate-700'
                              }`}
                              data-testid={`time-slot-${slotTime}`}
                            >
                              {slotTime}
                            </button>
                            
                            {isSlotHovered && !isSlotSelected && (
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 pointer-events-none">
                                <div className="bg-[#1a365d] text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap">
                                  <div className="font-medium mb-1">Na voljo:</div>
                                  <div>{slotTherapistNames}</div>
                                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#1a365d]" />
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-center py-3 text-slate-400 text-sm">
                      {therapistFilter !== 'all' ? 'Ta terapevt nima prostih terminov.' : 'Ni prostih terminov. Izberite drug datum.'}
                    </p>
                  )}
                </div>
              )}

              {selectedSlot && selectedTherapist && (
                <div className="bg-emerald-50 rounded-lg p-3 mb-3 flex items-center gap-3">
                  {selectedTherapist.photo ? (
                    <img loading="lazy" decoding="async" src={selectedTherapist.photo} alt="" className="w-10 h-10 rounded-full object-cover border-2 border-white shadow" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-emerald-200 flex items-center justify-center">
                      <User className="w-5 h-5 text-emerald-600" />
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-emerald-800">{selectedTime} - {selectedTherapist.name}</p>
                    <p className="text-xs text-emerald-600">{selectedTherapist.title || 'Fizioterapevt/ka'}</p>
                  </div>
                  <Check className="w-5 h-5 text-emerald-500 ml-auto" />
                </div>
              )}

              <div className="flex gap-2 pt-2 border-t">
                <Button variant="outline" size="sm" onClick={() => setStep(1)} className="flex-1">
                  <ChevronLeft className="w-4 h-4" /> Nazaj
                </Button>
                <Button size="sm" onClick={() => setStep(3)} disabled={!selectedDate || !selectedTime || !selectedTherapist} className="flex-1 bg-[#f87171] hover:bg-[#f87171]/90">
                  Naprej <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="step-details">
              <div className="bg-slate-50 rounded-lg p-2.5 mb-3 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Termin:</span><span className="font-medium">{selectedDate && format(selectedDate, 'd. MMMM yyyy', { locale: sl })} ob {selectedTime}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Storitev:</span><span className="font-medium">{selectedService?.name} - {selectedService?.price}€</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Terapevt:</span><span className="font-medium">{selectedTherapist?.name}</span></div>
              </div>

              <div className="space-y-2.5 mb-3">
                <div>
                  <label className="text-xs text-slate-500 flex items-center gap-1 mb-1"><User className="w-3 h-3" />Ime in priimek *</label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Janez Novak" className="h-9" data-testid="input-name" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-slate-500 flex items-center gap-1 mb-1"><Mail className="w-3 h-3" />E-pošta *</label>
                    <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="email@..." className="h-9" data-testid="input-email" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 flex items-center gap-1 mb-1"><Phone className="w-3 h-3" />Telefon *</label>
                    <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+386..." className="h-9" data-testid="input-phone" />
                  </div>
                </div>
                
                {/* Notes/Problems field */}
                <div>
                  <label className="text-xs text-slate-500 flex items-center gap-1 mb-1">
                    <MessageSquare className="w-3 h-3" />Opišite vaše težave (neobvezno)
                  </label>
                  <textarea 
                    value={formData.notes} 
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Na kratko opišite težave oz. bolečine ki vas mučijo..."
                    className="w-full h-20 px-3 py-2 text-sm border border-slate-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#f87171]/50 focus:border-[#f87171]"
                    data-testid="input-notes"
                  />
                </div>

                {/* File attachments */}
                <div>
                  <label className="text-xs text-slate-500 flex items-center gap-1 mb-2">
                    <Paperclip className="w-3 h-3" />Priložite izvide (neobvezno)
                  </label>
                  
                  {/* Upload button */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.webp"
                    multiple
                    className="hidden"
                    data-testid="file-input"
                  />
                  
                  {attachments.length < 3 && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingFile}
                      className="w-full border-2 border-dashed border-slate-200 rounded-lg p-3 hover:border-[#f87171]/50 hover:bg-slate-50 transition-colors flex items-center justify-center gap-2 text-sm text-slate-500"
                    >
                      {uploadingFile ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Nalagam...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4" />
                          Dodaj PDF, Word ali sliko (max 5MB)
                        </>
                      )}
                    </button>
                  )}
                  
                  {/* Uploaded files list */}
                  {attachments.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {attachments.map((file) => (
                        <div key={file.id} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
                          {file.type.includes('pdf') ? (
                            <FileText className="w-4 h-4 text-red-500" />
                          ) : file.type.includes('word') || file.type.includes('officedocument') ? (
                            <FileText className="w-4 h-4 text-blue-600" />
                          ) : (
                            <ImageIcon className="w-4 h-4 text-blue-500" />
                          )}
                          <span className="text-sm text-slate-700 flex-1 truncate">{file.name}</span>
                          <span className="text-xs text-slate-400">{formatFileSize(file.size)}</span>
                          <button
                            type="button"
                            onClick={() => removeAttachment(file.id)}
                            className="p-1 hover:bg-slate-200 rounded"
                          >
                            <X className="w-3 h-3 text-slate-500" />
                          </button>
                        </div>
                      ))}
                      <p className="text-xs text-slate-400 text-center">
                        {attachments.length}/3 datotek
                      </p>
                    </div>
                  )}
                </div>

                {/* Remember me checkbox */}
                <div className="space-y-2">
                  <label className="flex items-start gap-2 cursor-pointer group">
                    <input 
                      type="checkbox" 
                      checked={rememberMe} 
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="w-4 h-4 mt-0.5 rounded border-slate-300 text-[#f87171] focus:ring-[#f87171]/50"
                      data-testid="remember-me"
                    />
                    <span className="text-xs text-slate-500 group-hover:text-slate-700">
                      Zapomni si moje podatke za naslednjič
                    </span>
                  </label>
                  {rememberMe && (
                    <p className="text-[10px] text-slate-400 leading-tight pl-6">
                      Vaši podatki bodo shranjeni v brskalniku za hitrejše naročanje.
                    </p>
                  )}
                </div>
                
                {/* GDPR consent checkbox - required */}
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <label className="flex items-start gap-2 cursor-pointer group">
                    <input 
                      type="checkbox" 
                      checked={termsAccepted} 
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      className="w-4 h-4 mt-0.5 rounded border-slate-300 text-[#f87171] focus:ring-[#f87171]/50"
                      data-testid="terms-accepted"
                    />
                    <span className="text-xs text-slate-600 leading-relaxed">
                      Z oddajo rezervacije potrjujem, da sem seznanjen/a s <a href="/splosni-pogoji" className="text-[#f87171] underline hover:text-[#f87171]/80">splošnimi pogoji</a> ter z obdelavo osebnih podatkov za namen izvedbe storitve in <strong>obveščanja o strokovnih novostih</strong> (odjava kadarkoli). Več v <a href="/zasebnost" className="text-[#f87171] underline hover:text-[#f87171]/80">politiki zasebnosti</a>. *
                    </span>
                  </label>
                </div>
              </div>

              <div className="flex gap-2 pt-2 border-t">
                <Button variant="outline" size="sm" onClick={() => setStep(2)} className="flex-1"><ChevronLeft className="w-4 h-4" /> Nazaj</Button>
                <Button size="sm" onClick={() => setStep(4)} disabled={!formData.name || !formData.email || !formData.phone || !termsAccepted} className="flex-1 bg-[#f87171] hover:bg-[#f87171]/90">Naprej <ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div data-testid="step-payment">
              <div className="bg-gradient-to-r from-[#1a365d]/5 to-[#f87171]/5 rounded-lg p-3 mb-3">
                <p className="font-semibold text-[#1a365d] text-sm">Povzetek</p>
                <p className="text-xs text-slate-600">{selectedService?.name} • {selectedDate && format(selectedDate, 'd. MMMM yyyy', { locale: sl })} • {selectedTime}</p>
                <p className="text-xs text-slate-600">Terapevt: {selectedTherapist?.name}</p>
                <p className="text-xs text-slate-600">{formData.name} • {formData.phone}</p>
                {voucherDiscount > 0 ? (
                  <div className="mt-1">
                    <p className="text-sm text-slate-400 line-through">{servicePrice}€</p>
                    <div className="flex items-center gap-2">
                      <p className="text-xl font-bold text-[#f87171]">{finalPrice > 0 ? `${finalPrice}€` : 'Brezplačno'}</p>
                      <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">-{voucherDiscount}€ bon</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-xl font-bold text-[#f87171] mt-1">{servicePrice}€</p>
                )}
              </div>

              {/* Voucher code input */}
              <div className="mb-3">
                <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                  <Gift className="w-3 h-3" /> Imate darilni bon?
                </p>
                {voucherInfo?.valid ? (
                  <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                    <Tag className="w-4 h-4 text-emerald-600" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-emerald-800">{voucherCode.toUpperCase()}</p>
                      <p className="text-xs text-emerald-600">Popust: -{voucherDiscount}€</p>
                    </div>
                    <button onClick={clearVoucher} className="text-slate-400 hover:text-slate-600">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input 
                      value={voucherCode} 
                      onChange={(e) => setVoucherCode(e.target.value.toUpperCase())} 
                      placeholder="Vnesite kodo bona" 
                      className="h-9 flex-1 uppercase" 
                      data-testid="voucher-code-input"
                      onKeyDown={(e) => e.key === 'Enter' && validateVoucher()}
                    />
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={validateVoucher} 
                      disabled={!voucherCode.trim() || validatingVoucher}
                      className="h-9"
                      data-testid="voucher-validate-btn"
                    >
                      {validatingVoucher ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Preveri'}
                    </Button>
                  </div>
                )}
              </div>

              {/* Payment method - only show if voucher doesn't cover full price */}
              {!voucherCoversAll && (
                <>
                  <p className="text-xs text-slate-500 mb-2">Način plačila:</p>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <button onClick={() => setPaymentMethod('location')} className={`p-3 rounded-lg border-2 transition-all ${paymentMethod === 'location' ? 'border-[#f87171] bg-[#f87171]/5' : 'border-slate-200'}`} data-testid="payment-location">
                      <Banknote className={`w-5 h-5 mx-auto mb-1 ${paymentMethod === 'location' ? 'text-[#f87171]' : 'text-slate-400'}`} />
                      <p className="text-xs font-medium">Na lokaciji</p>
                    </button>
                    <button onClick={() => setPaymentMethod('card')} className={`p-3 rounded-lg border-2 transition-all ${paymentMethod === 'card' ? 'border-[#f87171] bg-[#f87171]/5' : 'border-slate-200'}`} data-testid="payment-card">
                      <CreditCard className={`w-5 h-5 mx-auto mb-1 ${paymentMethod === 'card' ? 'text-[#f87171]' : 'text-slate-400'}`} />
                      <p className="text-xs font-medium">Kartica</p>
                    </button>
                    <button onClick={() => setPaymentMethod('paypal')} className={`p-3 rounded-lg border-2 transition-all ${paymentMethod === 'paypal' ? 'border-[#0070ba] bg-[#0070ba]/5' : 'border-slate-200'}`} data-testid="payment-paypal">
                      <svg className={`w-5 h-5 mx-auto mb-1 ${paymentMethod === 'paypal' ? 'text-[#0070ba]' : 'text-slate-400'}`} viewBox="0 0 24 24" fill="currentColor"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.384a.77.77 0 0 1 .757-.645h6.927c2.33 0 4.127.636 5.34 1.89 1.127 1.165 1.607 2.833 1.432 4.965-.268 3.27-2.177 5.178-4.588 6.007-1.104.38-2.305.572-3.558.572H8.818a.77.77 0 0 0-.757.645l-.985 4.52zm5.592-9.753c1.922 0 3.06-1.277 3.232-2.903.09-.833-.088-1.493-.528-1.96-.44-.466-1.12-.702-2.024-.702H11.56l-.704 5.565h1.812z"/></svg>
                      <p className="text-xs font-medium">PayPal</p>
                    </button>
                  </div>
                </>
              )}

              {voucherCoversAll && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-3 text-center">
                  <p className="text-sm font-semibold text-emerald-700">Plačano z darilnim bonom</p>
                  <p className="text-xs text-emerald-600">Celoten znesek je pokrit z bonom</p>
                </div>
              )}

              <div className="flex gap-2 pt-2 border-t">
                <Button variant="outline" size="sm" onClick={() => setStep(3)} className="flex-1"><ChevronLeft className="w-4 h-4" /> Nazaj</Button>
                <Button size="sm" onClick={handleSubmit} disabled={isSubmitting} className="flex-1 bg-[#f87171] hover:bg-[#f87171]/90" data-testid="confirm-booking-btn">
                  {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Potrdi</>}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;
